import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  Clock,
  LogOut,
  ShoppingCart,
  User,
  Settings as SettingsIcon,
  Menu,
  X,
  AlertTriangle,
} from "lucide-react";
import SleekSignOutDialog from "../../components/ui/SleekSignOutDialog.jsx";
import { getBorrowerEmail } from "../../utils/auth";
import { signOut } from "../../api/client.js";
import { usePortalSessionGuard } from "../../hooks/usePortalSessionGuard.js";
import { shortlistPathForCategory } from "../../utils/sourcingCategory.js";

/* Mirror exact blue/orange animation from sourcing page cart (borderShine + cart-shiny-border, slicedWhiteFade + cart-text-fade) */
const shortlistNavStyles = `
  @keyframes portalBorderShine {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  @keyframes portalSlicedWhiteFade {
    0% { background-position: -300% 0; }
    50% { background-position: 300% 0; }
    100% { background-position: -300% 0; }
  }
  .portal-shortlist-shiny-border {
    position: relative;
    overflow: visible;
  }
  .portal-shortlist-shiny-border::before {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 1.25rem;
    background: linear-gradient(
      90deg,
      transparent 0%,
      transparent 20%,
      rgba(14, 165, 233, 0.7) 28%,
      rgba(56, 189, 248, 0.9) 35%,
      rgba(14, 165, 233, 1) 42%,
      rgba(56, 189, 248, 1) 48%,
      rgba(251, 191, 36, 1) 52%,
      rgba(245, 158, 11, 1) 58%,
      rgba(251, 191, 36, 0.9) 65%,
      rgba(245, 158, 11, 0.7) 72%,
      transparent 80%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: portalBorderShine 3s linear infinite;
    z-index: 0;
    padding: 3px;
    box-sizing: border-box;
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    -webkit-mask-composite: exclude;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
  }
  .portal-shortlist-shiny-border .portal-shortlist-link-inner {
    position: relative;
    z-index: 1;
  }
  .portal-shortlist-text-fade {
    position: relative;
    display: inline-block;
  }
  .portal-shortlist-text-fade::before {
    content: attr(data-text);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      transparent 30%,
      rgba(255, 255, 255, 0.3) 35%,
      rgba(255, 255, 255, 0.5) 40%,
      rgba(255, 255, 255, 0.7) 45%,
      rgba(255, 255, 255, 0.9) 50%,
      rgba(255, 255, 255, 0.7) 55%,
      rgba(255, 255, 255, 0.5) 60%,
      rgba(255, 255, 255, 0.3) 65%,
      transparent 70%,
      transparent 100%
    );
    background-size: 300% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: portalSlicedWhiteFade 30s linear infinite;
    pointer-events: none;
  }
`;

export default function PortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  usePortalSessionGuard("borrower");
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shortlistFullWarning, setShortlistFullWarning] = useState(false);

  const userEmail = getBorrowerEmail() || "Borrower";
  const shortlistTo = shortlistPathForCategory();

  // One-time warning after login when shortlist was full and sync skipped
  React.useEffect(() => {
    try {
      if (sessionStorage.getItem("ds_shortlist_full_warning") === "1") {
        setShortlistFullWarning(true);
      }
    } catch { /* ignore */ }
  }, []);
  
  // Hide sidebar when on application form route (all steps: borrower type selection, form, review)
  // Check for exact match or any path that includes /borrower/apply
  const isApplicationForm = location.pathname === "/borrower/apply" || 
                            location.pathname.startsWith("/borrower/apply/") ||
                            location.pathname.includes("/borrower/apply");

  const linkBase =
    "flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-50";
  const linkActive = "bg-sky-100 text-sky-900 font-semibold hover:bg-sky-100";

  const handleSignOutClick = () => {
    setShowSignOutModal(true);
  };

  const handleConfirmSignOut = async () => {
    await signOut();
    navigate("/borrower/signin");
  };

  return (
    <div className="bg-slate-50 min-h-dvh sm:min-h-dvh overflow-auto">
      <style>{shortlistNavStyles}</style>
      <div className="h-full min-h-0 flex flex-col md:flex-row">
        {!isApplicationForm && (
          <>
            {/* Mobile header */}
            <header className="md:hidden shrink-0 flex items-center justify-between gap-3 px-4 py-3 bg-white border-b border-slate-200">
              <button
                type="button"
                className="p-2 -ml-2 rounded-xl text-slate-600 hover:bg-slate-100"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>
              <span className="text-base font-bold text-slate-900">Borrower portal</span>
              <div className="w-10" aria-hidden />
            </header>
            {/* Mobile drawer */}
            {mobileMenuOpen && (
              <div className="fixed inset-0 z-50 md:hidden">
                <div
                  className="absolute inset-0 bg-black/50"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-hidden
                />
                <div className="absolute left-0 top-0 bottom-0 w-[280px] max-w-[85vw] bg-white shadow-xl overflow-y-auto">
                  <div className="flex items-center justify-between p-4 border-b border-slate-200">
                    <span className="font-bold text-slate-900">Menu</span>
                    <button
                      type="button"
                      className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"
                      onClick={() => setMobileMenuOpen(false)}
                      aria-label="Close menu"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="p-4">
                    <button
                      type="button"
                      onClick={() => { setMobileMenuOpen(false); window.location.assign("/"); }}
                      className="flex items-center gap-1.5 px-2 py-3 mb-4 w-full rounded-xl hover:bg-slate-50 text-left"
                      aria-label="Go to homepage"
                    >
                      <img src="/dynesourcer-logo.svg" alt="DyneSourcer" className="h-12 w-auto flex-shrink-0 object-contain" />
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-slate-900">Borrower portal</div>
                        <div className="text-xs text-slate-500 truncate">{userEmail}</div>
                      </div>
                    </button>
                    <nav className="space-y-1">
                      <NavLink to="/borrower/dashboard" end className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`} onClick={() => setMobileMenuOpen(false)}>
                        <Home className="w-4 h-4 shrink-0" /><span className="truncate">Dashboard</span>
                      </NavLink>
                      <div className="overflow-visible p-[3px]">
                        <NavLink to={shortlistTo} className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""} portal-shortlist-shiny-border`} onClick={() => setMobileMenuOpen(false)}>
                          <span className="portal-shortlist-link-inner flex items-center gap-3 w-full">
                            <ShoppingCart className="w-4 h-4 shrink-0" />
                            <span className="truncate relative inline-block portal-shortlist-text-fade" data-text="Shortlisted products">Shortlisted products</span>
                          </span>
                        </NavLink>
                      </div>
                      <NavLink to="/borrower/applications" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`} onClick={() => setMobileMenuOpen(false)}>
                        <Clock className="w-4 h-4 shrink-0" /><span className="truncate">Track Applications</span>
                      </NavLink>
                      <NavLink to="/borrower/profile" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`} onClick={() => setMobileMenuOpen(false)}>
                        <User className="w-4 h-4 shrink-0" /><span className="truncate">Profile</span>
                      </NavLink>
                      <NavLink to="/borrower/settings" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`} onClick={() => setMobileMenuOpen(false)}>
                        <SettingsIcon className="w-4 h-4 shrink-0" /><span className="truncate">Settings</span>
                      </NavLink>
                      <div className="border-t border-slate-200 my-2" />
                      <button type="button" onClick={handleSignOutClick} className={`${linkBase} text-rose-600 hover:text-rose-700`}>
                        <LogOut className="w-4 h-4 shrink-0" /><span className="truncate">Sign out</span>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
            {/* Desktop sidebar */}
            <aside className="hidden md:flex w-[260px] shrink-0 h-full overflow-y-auto bg-white border-r border-slate-200">
              <div className="p-4">
              {/* Home logo: polar bear, click → homepage */}
              <button
                type="button"
                onClick={() => window.location.assign("/")}
                className="flex items-center gap-1.5 px-2 py-3 w-full rounded-xl hover:bg-slate-50 transition-colors text-left"
                aria-label="Go to homepage"
              >
                <img
                  src="/dynesourcer-logo.svg"
                  alt="DyneSourcer"
                  className="h-14 w-auto flex-shrink-0 object-contain"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-base font-bold leading-tight text-slate-900">
                    Borrower portal
                  </div>
                  <div className="text-xs text-slate-500 truncate">{userEmail}</div>
                </div>
              </button>

              <nav className="mt-4 space-y-2">
                <NavLink
                  to="/borrower/dashboard"
                  end
                  className={({ isActive }) =>
                    `${linkBase} ${isActive ? linkActive : ""}`
                  }
                >
                  <Home className="w-4 h-4" />
                  <span className="truncate">Dashboard</span>
                </NavLink>

                <div className="overflow-visible p-[3px]">
                  <NavLink
                    to={shortlistTo}
                    className={({ isActive }) =>
                      `${linkBase} ${isActive ? linkActive : ""} portal-shortlist-shiny-border`
                    }
                  >
                    <span className="portal-shortlist-link-inner flex items-center gap-3 w-full">
                      <ShoppingCart className="w-4 h-4 shrink-0" />
                      <span className="truncate relative inline-block portal-shortlist-text-fade" data-text="Shortlisted products">Shortlisted products</span>
                    </span>
                  </NavLink>
                </div>

                <NavLink
                  to="/borrower/applications"
                  className={({ isActive }) =>
                    `${linkBase} ${isActive ? linkActive : ""}`
                  }
                >
                  <Clock className="w-4 h-4" />
                  <span className="truncate">Track Applications</span>
                </NavLink>

                <NavLink
                  to="/borrower/profile"
                  className={({ isActive }) =>
                    `${linkBase} ${isActive ? linkActive : ""}`
                  }
                >
                  <User className="w-4 h-4" />
                  <span className="truncate">Profile</span>
                </NavLink>

                <NavLink
                  to="/borrower/settings"
                  className={({ isActive }) =>
                    `${linkBase} ${isActive ? linkActive : ""}`
                  }
                >
                  <SettingsIcon className="w-4 h-4" />
                  <span className="truncate">Settings</span>
                </NavLink>

                <div className="border-t border-slate-200 my-2" />

                <button
                  type="button"
                  onClick={handleSignOutClick}
                  className={`${linkBase} text-rose-600 hover:text-rose-700`}
                >
                  <LogOut className="w-4 h-4" />
                  <span className="truncate">Sign out</span>
                </button>
              </nav>
            </div>
          </aside>
          </>
        )}

        <main className={`${isApplicationForm ? "w-full flex-1 min-w-0 h-full overflow-y-auto px-4 sm:px-6 md:px-8 py-4" : "flex-1 min-w-0 flex-1 h-full overflow-y-auto p-4 sm:p-6"}`}>
          {shortlistFullWarning && (
            <div className="mb-4 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
              <p className="text-sm font-medium">
                Your shortlist is full (5 products). Remove one from Shortlisted products to add more from Sourcing.
              </p>
              <button
                type="button"
                className="ml-auto shrink-0 rounded-lg p-1.5 text-amber-600 hover:bg-amber-100"
                onClick={() => {
                  setShortlistFullWarning(false);
                  try {
                    sessionStorage.removeItem("ds_shortlist_full_warning");
                  } catch { /* ignore */ }
                }}
                aria-label="Dismiss"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}
          <Outlet />
        </main>
      </div>

      {/* Sign Out Confirmation Modal */}
      <SleekSignOutDialog
        isOpen={showSignOutModal}
        onClose={() => setShowSignOutModal(false)}
        onConfirm={handleConfirmSignOut}
      />
    </div>
  );
}
